import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Dialog,
  Button,
  Body2,
  EyeOffIcon,
  RecurringCheckmarkIcon,
  ChecklistIcon,
  DependencyIcon,
  MessageIcon,
} from "@activecollab/components";
import { DragProvider, ReorderableList, reorderArray } from "@activecollab/yank-and-drop";
import cloneDeep from "lodash/cloneDeep";
import PropTypes from "prop-types";
import { columnsMapByType } from "~/components/ListView/common";
import { DraggableColumn } from "~/components/ListView/view-settings-dialog/DraggableColumn";
import { ViewSettingsListItem } from "~/components/ListView/view-settings-dialog/ViewSettingsListItem";
import { useMyTasksColumnsContext } from "~/components/MyWork/MyTasks/context";
import i18n from "~/i18n";
import { useMyWorkDisplaySettingsMutation } from "~/repository";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions";

const signifiersMapByName = {
  comments: {
    label: i18n.t("Comments"),
    Icon: MessageIcon,
  },
  dependencies: {
    label: i18n.t("Dependencies"),
    Icon: DependencyIcon,
  },
  subtasks: {
    label: i18n.t("Subtasks"),
    Icon: ChecklistIcon,
  },
  client_visibility: {
    label: i18n.t("Visibility to client"),
    Icon: EyeOffIcon,
  },
  recurrence: {
    label: i18n.t("Recurrence"),
    Icon: RecurringCheckmarkIcon,
  },
};

export const MyTasksViewSettingsDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));

  const data = useMyTasksColumnsContext();

  const [formState, setFormState] = useState(() => {
    const initialFormState = cloneDeep(data);
    if (!hideFromClients) {
      initialFormState.task_signifiers = initialFormState.task_signifiers.filter((item) => {
        return item.name !== "client_visibility";
      });
    }
    return initialFormState;
  });

  const updateMutation = useMyWorkDisplaySettingsMutation();

  const { task_signifiers: signifiers, columns } = formState;

  const [cols, setCols] = useState(columns);

  const onReorder = useCallback((from, to) => {
    setCols((prevState) => reorderArray(prevState, from, to));
  }, []);

  const handleChangeColumns = useCallback((event) => {
    let type = event.target.id;
    type = type.split("-")[2];

    setCols((prevState) => {
      const newState = cloneDeep(prevState);
      const newValue = newState.find((item) => item.name === type);
      newValue.value = !newValue.value;
      return newState;
    });
  }, []);

  const handleChangeSignifiers = useCallback((event) => {
    let name = event.target.id;
    name = name.split("-")[2];

    setFormState((prevState) => {
      const newState = cloneDeep(prevState);
      const newValue = newState["task_signifiers"].find((item) => item.name === name);
      newValue.value = !newValue.value;
      return newState;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    updateMutation.mutate({ ...formState, columns: cols });
    onClose();
  }, [cols, formState, onClose, updateMutation]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      enableBackgroundClick
    >
      <Dialog.Title>{t("Settings")}</Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content className="tw-flex tw-flex-col">
        <Body2
          weight="bold"
          className="tw-mb-3"
        >
          {t("Columns")}
        </Body2>
        <DragProvider>
          <ReorderableList
            dragAxis="y"
            onMove={onReorder}
          >
            {cols.map(({ name, value, active }, index) => {
              if (active)
                return (
                  <DraggableColumn
                    key={`column-settings-${index}`}
                    index={index}
                  >
                    <ViewSettingsListItem
                      id={`column-settings-${name}`}
                      label={columnsMapByType[name] ? columnsMapByType[name] : name.toLowerCase()}
                      value={value}
                      onChange={handleChangeColumns}
                      className="tw-pl-6"
                    />
                  </DraggableColumn>
                );
            })}
          </ReorderableList>
        </DragProvider>
        <Body2
          weight="bold"
          className="tw-my-3"
        >
          {t("Task Signifiers")}
        </Body2>
        {signifiers.map(({ name, value }, index) => {
          const { label, Icon } = signifiersMapByName[name];
          return (
            <ViewSettingsListItem
              key={`signifier-settings-${index}`}
              id={`signifier-settings-${name}`}
              label={label}
              value={value}
              onChange={handleChangeSignifiers}
              Icon={Icon}
              iconClassName="tw-fill-theme-900"
              className="hover:tw-bg-theme-200"
            />
          );
        })}
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          variant="primary"
          className="tw-mr-3"
          onClick={handleSubmit}
        >
          {t("Save")}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {t("Cancel")}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

MyTasksViewSettingsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

MyTasksViewSettingsDialog.defaultProps = {
  open: false,
};

MyTasksViewSettingsDialog.displayName = "MyTasksViewSettingsDialog";
